/**
 * نظام تحسين الأداء الشامل
 * يتضمن تحسينات للذاكرة، التخزين المؤقت، وتحميل البيانات
 */

import { useCallback, useMemo, useRef, useEffect, useState } from 'react';

// تخزين مؤقت للبيانات
interface CacheItem<T> {
  data: T;
  timestamp: number;
  expiry: number;
}

class PerformanceCache {
  private cache = new Map<string, CacheItem<any>>();
  private maxSize = 100; // الحد الأقصى لعدد العناصر المخزنة

  set<T>(key: string, data: T, ttl: number = 5 * 60 * 1000): void {
    // إزالة العناصر القديمة إذا تجاوز الحد الأقصى
    if (this.cache.size >= this.maxSize) {
      const oldestKey = this.cache.keys().next().value;
      this.cache.delete(oldestKey);
    }

    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      expiry: Date.now() + ttl
    });
  }

  get<T>(key: string): T | null {
    const item = this.cache.get(key);

    if (!item) {
      return null;
    }

    // التحقق من انتهاء صلاحية البيانات
    if (Date.now() > item.expiry) {
      this.cache.delete(key);
      return null;
    }

    return item.data;
  }

  clear(): void {
    this.cache.clear();
  }

  delete(key: string): boolean {
    return this.cache.delete(key);
  }

  // تنظيف البيانات المنتهية الصلاحية
  cleanup(): void {
    const now = Date.now();
    this.cache.forEach((item, key) => {
      if (now > item.expiry) {
        this.cache.delete(key);
      }
    });
  }
}

// إنشاء مثيل واحد للتخزين المؤقت
export const performanceCache = new PerformanceCache();

// تنظيف دوري للتخزين المؤقت
setInterval(() => {
  performanceCache.cleanup();
}, 60000); // كل دقيقة

/**
 * Hook لتحسين الاستعلامات المتكررة
 */
export const useOptimizedQuery = <T>(
  queryFn: () => Promise<T>,
  dependencies: any[],
  cacheKey: string,
  ttl: number = 5 * 60 * 1000
) => {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const memoizedQueryFn = useCallback(queryFn, dependencies);

  const executeQuery = useCallback(async () => {
    // إلغاء الطلب السابق إذا كان موجوداً
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // التحقق من وجود البيانات في التخزين المؤقت
    const cachedData = performanceCache.get<T>(cacheKey);
    if (cachedData) {
      setData(cachedData);
      return;
    }

    setLoading(true);
    setError(null);
    abortControllerRef.current = new AbortController();

    try {
      const result = await memoizedQueryFn();

      // التحقق من عدم إلغاء الطلب
      if (!abortControllerRef.current.signal.aborted) {
        setData(result);
        performanceCache.set(cacheKey, result, ttl);
      }
    } catch (err) {
      if (!abortControllerRef.current.signal.aborted) {
        setError(err instanceof Error ? err.message : 'حدث خطأ غير متوقع');
      }
    } finally {
      if (!abortControllerRef.current.signal.aborted) {
        setLoading(false);
      }
    }
  }, [memoizedQueryFn, cacheKey, ttl]);

  useEffect(() => {
    executeQuery();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [executeQuery]);

  return { data, loading, error, refetch: executeQuery };
};

/**
 * Hook لتحسين التصفية والبحث
 */
export const useOptimizedFilter = <T>(
  items: T[],
  filterFn: (item: T, query: string) => boolean,
  searchQuery: string,
  debounceMs: number = 300
) => {
  const [debouncedQuery, setDebouncedQuery] = useState(searchQuery);

  // تأخير البحث لتحسين الأداء
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [searchQuery, debounceMs]);

  // تصفية محسنة مع memoization
  const filteredItems = useMemo(() => {
    if (!debouncedQuery.trim()) {
      return items;
    }

    return items.filter(item => filterFn(item, debouncedQuery));
  }, [items, filterFn, debouncedQuery]);

  return filteredItems;
};

/**
 * Hook لتحسين التحميل التدريجي
 */
export const useVirtualizedList = <T>(
  items: T[],
  itemHeight: number,
  containerHeight: number,
  overscan: number = 5
) => {
  const [scrollTop, setScrollTop] = useState(0);

  const visibleRange = useMemo(() => {
    const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
    const endIndex = Math.min(
      items.length - 1,
      Math.ceil((scrollTop + containerHeight) / itemHeight) + overscan
    );

    return { startIndex, endIndex };
  }, [scrollTop, itemHeight, containerHeight, items.length, overscan]);

  const visibleItems = useMemo(() => {
    return items.slice(visibleRange.startIndex, visibleRange.endIndex + 1);
  }, [items, visibleRange]);

  const totalHeight = items.length * itemHeight;
  const offsetY = visibleRange.startIndex * itemHeight;

  return {
    visibleItems,
    totalHeight,
    offsetY,
    setScrollTop,
    visibleRange
  };
};

/**
 * تحسين تحميل الصور
 */
export const useImageOptimization = () => {
  const imageCache = useRef(new Map<string, HTMLImageElement>());

  const preloadImage = useCallback((src: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (imageCache.current.has(src)) {
        resolve();
        return;
      }

      const img = new Image();
      img.onload = () => {
        imageCache.current.set(src, img);
        resolve();
      };
      img.onerror = reject;
      img.src = src;
    });
  }, []);

  const preloadImages = useCallback(async (sources: string[]) => {
    const promises = sources.map(src => preloadImage(src));
    await Promise.allSettled(promises);
  }, [preloadImage]);

  return { preloadImage, preloadImages };
};

/**
 * تحسين استخدام الذاكرة
 */
export const useMemoryOptimization = () => {
  const cleanup = useCallback(() => {
    // تنظيف التخزين المؤقت
    performanceCache.cleanup();

    // تنظيف localStorage من البيانات القديمة
    const now = Date.now();
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.endsWith('_expiry')) {
        const expiry = parseInt(localStorage.getItem(key) || '0');
        if (now > expiry) {
          const dataKey = key.replace('_expiry', '');
          localStorage.removeItem(key);
          localStorage.removeItem(dataKey);
        }
      }
    }
  }, []);

  useEffect(() => {
    // تنظيف دوري
    const interval = setInterval(cleanup, 5 * 60 * 1000); // كل 5 دقائق

    return () => clearInterval(interval);
  }, [cleanup]);

  return { cleanup };
};

/**
 * تحسين طلبات الشبكة
 */
export const useNetworkOptimization = () => {
  const requestQueue = useRef<Map<string, Promise<any>>>(new Map());

  const optimizedFetch = useCallback(async <T>(
    url: string,
    options?: RequestInit,
    cacheKey?: string
  ): Promise<T> => {
    const key = cacheKey || url;

    // التحقق من وجود طلب مماثل قيد التنفيذ
    if (requestQueue.current.has(key)) {
      return requestQueue.current.get(key);
    }

    // التحقق من التخزين المؤقت
    if (cacheKey) {
      const cached = performanceCache.get<T>(cacheKey);
      if (cached) {
        return cached;
      }
    }

    // تنفيذ الطلب
    const promise = fetch(url, options)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        // حفظ في التخزين المؤقت
        if (cacheKey) {
          performanceCache.set(cacheKey, data);
        }
        return data;
      })
      .finally(() => {
        // إزالة من قائمة الانتظار
        requestQueue.current.delete(key);
      });

    requestQueue.current.set(key, promise);
    return promise;
  }, []);

  return { optimizedFetch };
};

/**
 * مراقب الأداء
 */
export const usePerformanceMonitor = () => {
  const metrics = useRef({
    renderCount: 0,
    lastRenderTime: 0,
    averageRenderTime: 0
  });

  const startMeasure = useCallback((name: string) => {
    performance.mark(`${name}-start`);
  }, []);

  const endMeasure = useCallback((name: string) => {
    performance.mark(`${name}-end`);
    performance.measure(name, `${name}-start`, `${name}-end`);

    const measure = performance.getEntriesByName(name)[0];
    console.log(`Performance: ${name} took ${measure.duration.toFixed(2)}ms`);

    return measure.duration;
  }, []);

  const trackRender = useCallback(() => {
    metrics.current.renderCount++;
    const now = performance.now();

    if (metrics.current.lastRenderTime > 0) {
      const renderTime = now - metrics.current.lastRenderTime;
      metrics.current.averageRenderTime =
        (metrics.current.averageRenderTime + renderTime) / 2;
    }

    metrics.current.lastRenderTime = now;
  }, []);

  return { startMeasure, endMeasure, trackRender, metrics: metrics.current };
};

// تصدير الوظائف المساعدة
export const PerformanceUtils = {
  // تأخير التنفيذ
  debounce: <T extends (...args: any[]) => any>(
    func: T,
    wait: number
  ): ((...args: Parameters<T>) => void) => {
    let timeout: NodeJS.Timeout;
    return (...args: Parameters<T>) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  },

  // تقليل عدد الاستدعاءات
  throttle: <T extends (...args: any[]) => any>(
    func: T,
    limit: number
  ): ((...args: Parameters<T>) => void) => {
    let inThrottle: boolean;
    return (...args: Parameters<T>) => {
      if (!inThrottle) {
        func(...args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  },

  // تحسين حجم البيانات
  compressData: (data: any): string => {
    try {
      return JSON.stringify(data);
    } catch {
      return '';
    }
  },

  // إلغاء ضغط البيانات
  decompressData: <T>(compressed: string): T | null => {
    try {
      return JSON.parse(compressed);
    } catch {
      return null;
    }
  }
};

export default {
  performanceCache,
  useOptimizedQuery,
  useOptimizedFilter,
  useVirtualizedList,
  useImageOptimization,
  useMemoryOptimization,
  useNetworkOptimization,
  usePerformanceMonitor,
  PerformanceUtils
};
